<script setup>
import {ref, computed, watch, onMounted} from 'vue'

const emit = defineEmits(['gradientChanged'])

const props = defineProps({
    gradient: {
        type: Object,
        required: true
    }
})

const colorStops = ref([])

const gradientStyle = computed(() => {
    const stops = colorStops.value
        .map(stop => `${stop.color} ${stop.position}%`)
        .join(', ')
    return `linear-gradient(to bottom, ${stops})`
})

const addColorStop = () => {
    console.log(colorStops.value.length)
    if (colorStops.value.length === 0) {
        colorStops.value = [
            {color: 'rgba(0, 0, 0, 0)', position: 0},
            {color: 'rgba(0, 0, 0, 0.25)', position: 100}
        ]
        return
    }
    const lastStop = colorStops.value[colorStops.value.length - 1]
    const newPosition = Math.min(lastStop.position + 20, 100)
    colorStops.value.push({
        color: 'rgba(0, 0, 0, 0.5)',
        position: newPosition
    })
}

const resetColorStops = () => {
    colorStops.value = [
        {color: 'rgba(0, 0, 0, 0)', position: 0},
        {color: 'rgba(0, 0, 0, 0)', position: 100}
    ]
}

const removeColorStop = (index) => {
    if (colorStops.value.length > 2) {
        colorStops.value.splice(index, 1)
    }
}

const updateColor = (index, color) => {
    colorStops.value[index].color = color
}

const updatePosition = (index, position) => {
    colorStops.value[index].position = position
}

const removeAllColorStops = () => {
    colorStops.value = []
}

watch(() => colorStops.value, (newStops) => {
    const compositeGradient = {
        direction: 'to bottom',
        valid: true,
        colors: newStops.map(stop => stop.color),
        positions: newStops.map(stop => stop.position),
        positionsAsDecimals: newStops.map(stop => parseFloat((stop.position / 100).toFixed(2))),
        stops: newStops,
    }
    compositeGradient.colors = compositeGradient.colors.map(color => color || 'transparent')

    compositeGradient.stops = compositeGradient.stops.map(stop => {
        return {
            color: stop.color || 'transparent',
            position: stop.position
        }
    })

    compositeGradient.valid = compositeGradient.colors.some(color => color !== 'transparent')

    emit('gradientChanged', compositeGradient)
}, {deep: true})

onMounted(() => {
    if (props.gradient) {
        colorStops.value = props.gradient.stops
    } else {
        colorStops.value = [
            {color: 'rgba(0, 0, 0, 0)', position: 0},
            {color: 'rgba(0, 0, 0, 0.25)', position: 100}
        ]
    }
})

</script>

<template>
    <div>
        <div class="-mx-4 md:-mx-6 bg-gray-200">
            <div class="flex items-stretch">
                <div
                    class="p-2 pr-0 w-24">
                    <div
                        v-if="colorStops.length > 0"
                        class="rounded-wl mb-6 border border-gray-300 w-full h-full"
                        :style="{ background: gradientStyle }"
                    ></div>
                </div>
                <div class="w-full">
                    <div
                        v-for="(stop, index) in colorStops"
                        :key="index"
                        class="bg-white rounded-wl m-2 p-6 relative"
                    >
                        <button
                            v-if="colorStops.length > 2"
                            class="absolute top-1 right-2 text-myflowRed"
                            @click="removeColorStop(index)"
                        >
                            <i class="fa-regular fa-trash"></i>
                        </button>
                        <color-picker
                            :color="stop.color"
                            :debounce="100"
                            :disable-alpha="false"
                            :show-palette="true"
                            format="rgb"
                            @colorChanged="(color) => updateColor(index, color)"
                            @clear-color="updateColor(index, null)"
                        />
                        <div class="mt-4">
                            <input-label label="Position"/>
                            <div class="flex">
                                <input
                                    type="range"
                                    :value="stop.position"
                                    @input="(e) => updatePosition(index, parseInt(e.target.value))"
                                    min="0"
                                    max="100"
                                    class="w-full"
                                />
                                <span class="position-value">{{ stop.position }}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-2 pb-2 px-2 flex justify-end space-x-2">
                <button-danger
                    v-if="colorStops.length > 0"
                    size="sm"
                    @click="removeAllColorStops"
                >
                    {{ _mft('pagebuilder:styleEditor.setting.backgroundGradient.reset') }}
                </button-danger>
                <button-secondary size="sm" @click="addColorStop">
                    {{ _mft('pagebuilder:styleEditor.setting.backgroundGradient.addColorStop') }}
                </button-secondary>
            </div>
        </div>
    </div>
</template>

<style scoped>
.position-value {
    min-width: 48px;
    text-align: right;
}
</style>
